import React, { useState, useMemo } from "react";
import axios from "axios";
import {
  Box, Button, Card, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, Divider, Fade, Grid, IconButton, InputAdornment, MenuItem, Select,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Tooltip, Typography, alpha,
} from "@mui/material";
import dayjs from "dayjs";
import {
  useGetDriverQuery,
  useGetPayrollSummaryQuery,
  useGetPayrollIncomeQuery,
  useRecordPaymentMutation,
  useGetPayrollExpensesQuery,
  useStorePayrollExpenseMutation,
  useDeletePayrollExpenseMutation,
  useGetPayrollTransactionsQuery,
} from "../../Api/attoDeskApi";
import {
  IDriverPayrollIncome, IDriverPayrollExpense, IPayrollTransaction,
  IPayrollSummary, PaymentMethod, ExpenseType,
} from "../../Api/Interface/api.interface";
import { useNotifier } from "../../Core/Notifier";
import { fmtDriverName } from "../../Core/utils/nameUtils";

// Icons
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import PaymentsOutlinedIcon from "@mui/icons-material/PaymentsOutlined";
import FilterListIcon from "@mui/icons-material/FilterList";
import PrintIcon from "@mui/icons-material/Print";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import * as XLSX from "xlsx";

// ── Constants ──────────────────────────────────────────────────────────────────
const BRAND = "#2f3891";
const BORDER = "#e2e8f0";

const METHOD_CFG: Record<PaymentMethod, { label: string; color: string; bg: string; border: string; activeBorder: string; glow: string; icon: string }> = {
  cash:        { label: "Cash",        color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", activeBorder: "#16a34a", glow: "rgba(22,163,74,0.15)",  icon: "💵" },
  etransfer:   { label: "E-Transfer",  color: "#2f3891", bg: "#eef2ff", border: "#c7d2fe", activeBorder: "#2f3891", glow: "rgba(99,102,241,0.15)", icon: "🔄" },
  credit_card: { label: "Credit Card", color: "#0ea5e9", bg: "#f0f9ff", border: "#bae6fd", activeBorder: "#0ea5e9", glow: "rgba(14,165,233,0.15)", icon: "💳" },
  debit_card:  { label: "Debit Card",  color: "#2f3891", bg: "#f5f3ff", border: "#ddd6fe", activeBorder: "#2f3891", glow: "rgba(139,92,246,0.15)", icon: "🏧" },
  cheque:      { label: "Cheque",      color: "#f59e0b", bg: "#fffbeb", border: "#fde68a", activeBorder: "#f59e0b", glow: "rgba(245,158,11,0.15)", icon: "📝" },
};
const ALL_METHODS: PaymentMethod[] = ["cash", "etransfer", "credit_card", "debit_card", "cheque"];

const EXPENSE_TYPES: { value: ExpenseType; label: string }[] = [
  { value: "fuel",        label: "Fuel"        },
  { value: "repair",      label: "Repair"      },
  { value: "toll",        label: "Toll"        },
  { value: "maintenance", label: "Maintenance" },
  { value: "other",       label: "Other"       },
];

const thSx = {
  fontWeight: 700, fontSize: 11, color: "#64748b", textTransform: "uppercase" as const,
  letterSpacing: "0.06em", py: 1.25, px: 1.5, borderBottom: `1px solid ${BORDER}`,
  bgcolor: "#f8fafc", whiteSpace: "nowrap" as const,
};
const tdSx = {
  py: 1.25, px: 1.5, borderBottom: "1px solid #f1f5f9",
  fontSize: 12, color: "#1e293b", verticalAlign: "middle" as const,
};

const MethodChip: React.FC<{ method: PaymentMethod | null; onClick?: () => void }> = ({ method, onClick }) => {
  if (!method) return (
    <Box component="span" onClick={onClick}
      sx={{ display: "inline-flex", px: 1.25, py: 0.3, borderRadius: 1.5, fontSize: 11, fontWeight: 600,
        cursor: onClick ? "pointer" : "default", color: "#94a3b8", bgcolor: "#f1f5f9", border: "1px solid #e2e8f0",
        "&:hover": onClick ? { bgcolor: "#e2e8f0" } : {} }}>
      Set Method
    </Box>
  );
  const cfg = METHOD_CFG[method];
  return (
    <Box component="span" onClick={onClick}
      sx={{ display: "inline-flex", px: 1.25, py: 0.3, borderRadius: 1.5, fontSize: 11, fontWeight: 700,
        cursor: onClick ? "pointer" : "default", color: cfg.color, bgcolor: cfg.bg, border: `1px solid ${cfg.border}`,
        transition: "opacity 0.15s", "&:hover": onClick ? { opacity: 0.8 } : {} }}>
      {cfg.label}
    </Box>
  );
};

// ── Process Payment Modal ──────────────────────────────────────────────────────
const ProcessPaymentModal: React.FC<{
  income: IDriverPayrollIncome | null;
  onClose: () => void;
  onSaved: () => void;
}> = ({ income, onClose, onSaved }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [method, setMethod]     = useState<PaymentMethod>("cash");
  const [reference, setRef]     = useState("");
  const [notes, setNotes]       = useState("");
  const [saving, setSaving]     = useState(false);
  const [recordPayment]         = useRecordPaymentMutation();

  React.useEffect(() => {
    if (income) {
      setMethod(income.payment_method ?? "cash");
      setRef(income.payment_reference ?? "");
      setNotes(income.payment_notes ?? "");
    }
  }, [income]);

  const handleSave = async () => {
    if (!income) return;
    setSaving(true);
    const res: any = await recordPayment({ id: income.id, payment_method: method, payment_reference: reference, payment_notes: notes });
    if (res?.error || res?.data?.success === false) {
      showErrorMessage(res?.data?.message || "Failed to record payment.");
    } else {
      showMessage("Payment recorded successfully.");
      onSaved();
      onClose();
    }
    setSaving(false);
  };

  const isCreditCard = method === "credit_card" || method === "debit_card";

  return (
    <Dialog open={!!income} onClose={() => !saving && onClose()} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "#eef2ff", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <CreditCardOutlinedIcon sx={{ fontSize: 20, color: BRAND }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Process Payment</Typography>
            <Typography sx={{ fontSize: 12, color: "#64748b" }}>
              {income?.invoice_no || "Trip"} — ${income?.total_payment?.toFixed(2)}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
        {/* Method buttons */}
        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
            Payment Method
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {ALL_METHODS.map((m) => {
              const cfg = METHOD_CFG[m];
              return (
                <Box key={m} onClick={() => setMethod(m)}
                  sx={{ px: 1.5, py: 0.6, borderRadius: 2, fontSize: 12, fontWeight: 700, cursor: "pointer",
                    color: method === m ? cfg.color : "#64748b",
                    bgcolor: method === m ? cfg.bg : "#f8fafc",
                    border: `1.5px solid ${method === m ? cfg.border : "#e2e8f0"}`,
                    transition: "all 0.15s" }}>
                  {cfg.label}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Amount (read-only) */}
        <TextField size="small" fullWidth label="Amount" value={`$${income?.total_payment?.toFixed(2) ?? "0.00"}`}
          InputProps={{ readOnly: true, startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          sx={{ "& .MuiOutlinedInput-root": { bgcolor: "#f8fafc" } }} />

        {/* Card reference field */}
        <TextField size="small" fullWidth
          label={isCreditCard ? "Card Last 4 Digits / Reference" : "Reference / Cheque No. (optional)"}
          value={reference} onChange={(e) => setRef(e.target.value)}
          placeholder={isCreditCard ? "e.g. 4242" : "e.g. CHQ-001"} />

        <TextField size="small" fullWidth multiline rows={2} label="Notes (optional)"
          value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="Any additional payment notes..." />
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button size="small" onClick={onClose} disabled={saving}
          sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, color: "#64748b", border: `1px solid ${BORDER}` }}>
          Cancel
        </Button>
        <Button variant="contained" size="small" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} /> : <CreditCardOutlinedIcon />}
          sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, bgcolor: BRAND, "&:hover": { bgcolor: "#2f3891" } }}>
          {saving ? "Saving..." : "Confirm Payment"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Add Expense Modal ──────────────────────────────────────────────────────────
const AddExpenseModal: React.FC<{
  open: boolean;
  driverId: string;
  onClose: () => void;
  onSaved: () => void;
}> = ({ open, driverId, onClose, onSaved }) => {
  const { showMessage, showErrorMessage } = useNotifier();
  const [expType, setExpType]   = useState<ExpenseType>("fuel");
  const [amount, setAmount]     = useState("");
  const [method, setMethod]     = useState<PaymentMethod>("cash");
  const [date, setDate]         = useState(dayjs().format("YYYY-MM-DD"));
  const [notes, setNotes]       = useState("");
  const [saving, setSaving]     = useState(false);
  const [storeExpense]          = useStorePayrollExpenseMutation();

  const handleSave = async () => {
    if (!driverId || !amount || parseFloat(amount) <= 0) {
      showErrorMessage("Driver and a valid amount are required.");
      return;
    }
    setSaving(true);
    const res: any = await storeExpense({
      driver_id:      Number(driverId),
      expense_type:   expType,
      amount:         parseFloat(amount),
      payment_method: method,
      expense_date:   date,
      notes:          notes.trim() || undefined,
    });
    if (res?.error || res?.data?.success === false) {
      showErrorMessage(res?.data?.message || "Failed to save expense.");
    } else {
      showMessage("Expense recorded.");
      setAmount(""); setNotes(""); setExpType("fuel"); setMethod("cash"); setDate(dayjs().format("YYYY-MM-DD"));
      onSaved();
      onClose();
    }
    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={() => !saving && onClose()} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 36, height: 36, borderRadius: 2, bgcolor: "#fef2f2", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <TrendingDownIcon sx={{ fontSize: 20, color: "#ef4444" }} />
          </Box>
          <Typography sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>Add Expense</Typography>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent sx={{ pt: 2.5, display: "flex", flexDirection: "column", gap: 2 }}>
        <Select size="small" fullWidth value={expType} onChange={(e) => setExpType(e.target.value as ExpenseType)}
          sx={{ borderRadius: 2, fontSize: 13 }}>
          {EXPENSE_TYPES.map((t) => <MenuItem key={t.value} value={t.value} sx={{ fontSize: 13 }}>{t.label}</MenuItem>)}
        </Select>

        <TextField size="small" fullWidth label="Amount *" type="number"
          value={amount} onChange={(e) => setAmount(e.target.value)}
          InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
          inputProps={{ min: 0.01, step: 0.01 }} />

        <Box>
          <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.06em", mb: 1 }}>
            Payment Method *
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
            {ALL_METHODS.map((m) => {
              const cfg = METHOD_CFG[m];
              return (
                <Box key={m} onClick={() => setMethod(m)}
                  sx={{ px: 1.25, py: 0.5, borderRadius: 2, fontSize: 11, fontWeight: 700, cursor: "pointer",
                    color: method === m ? cfg.color : "#64748b",
                    bgcolor: method === m ? cfg.bg : "#f8fafc",
                    border: `1.5px solid ${method === m ? cfg.border : "#e2e8f0"}`,
                    transition: "all 0.15s" }}>
                  {cfg.label}
                </Box>
              );
            })}
          </Box>
        </Box>

        <TextField size="small" fullWidth type="date" label="Date *"
          value={date} onChange={(e) => setDate(e.target.value)}
          InputLabelProps={{ shrink: true }} />

        <TextField size="small" fullWidth multiline rows={2} label="Notes (optional)"
          value={notes} onChange={(e) => setNotes(e.target.value)}
          placeholder="e.g. Fuel at Shell station on Hwy 401" />
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button size="small" onClick={onClose} disabled={saving}
          sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, color: "#64748b", border: `1px solid ${BORDER}` }}>
          Cancel
        </Button>
        <Button variant="contained" size="small" onClick={handleSave} disabled={saving}
          startIcon={saving ? <CircularProgress size={14} /> : <AddIcon />}
          sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, bgcolor: "#ef4444", "&:hover": { bgcolor: "#dc2626" } }}>
          {saving ? "Saving..." : "Save Expense"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ── Main Page ──────────────────────────────────────────────────────────────────
const DriverPayrollPage: React.FC = () => {
  const { showMessage, showErrorMessage } = useNotifier();

  // Filters
  const [driverId, setDriverId]       = useState<string>("");
  const [dateFrom, setDateFrom]       = useState<string>("");
  const [dateTo, setDateTo]           = useState<string>("");
  const [methodFilter, setMethodFilter] = useState<string>("");

  // Modal state
  const [paymentIncome, setPaymentIncome] = useState<IDriverPayrollIncome | null>(null);
  const [expenseOpen, setExpenseOpen]     = useState(false);
  const [activeSection, setActiveSection] = useState<"income" | "expenses" | "transactions">("income");

  // Active card filter (client-side — no API round-trip)
  const [activeMethodCard, setActiveMethodCard] = useState<string | null>(null);

  const filters = useMemo(() => ({
    driver_id:      driverId  || undefined,
    date_from:      dateFrom  || undefined,
    date_to:        dateTo    || undefined,
    payment_method: methodFilter || undefined,
  }), [driverId, dateFrom, dateTo, methodFilter]);

  const { data: driverData } = useGetDriverQuery({ searchText: "", id: "" });
  const { data: summaryData, refetch: refetchSummary } = useGetPayrollSummaryQuery(filters);
  const { data: incomeData,  refetch: refetchIncome  } = useGetPayrollIncomeQuery(filters);
  const { data: expenseData, refetch: refetchExpenses } = useGetPayrollExpensesQuery(filters);
  const { data: txData,      refetch: refetchTx      } = useGetPayrollTransactionsQuery(filters);
  const [deleteExpense] = useDeletePayrollExpenseMutation();

  const drivers: any[] = useMemo(() => {
    if (!driverData || !Array.isArray(driverData.data)) return [];
    return driverData.data.filter((d: any) => d.driverUserRole === "driver");
  }, [driverData]);

  const summary: IPayrollSummary | null = (summaryData as any)?.data ?? null;
  const incomes: IDriverPayrollIncome[]     = useMemo(() => (incomeData as any)?.data ?? [], [incomeData]);
  const expenses: IDriverPayrollExpense[]   = useMemo(() => (expenseData as any)?.data ?? [], [expenseData]);
  const transactions: IPayrollTransaction[] = useMemo(() => (txData as any)?.data ?? [], [txData]);

  // ── Per-method counts (for card badges) ──────────────────────────────────────
  const methodStats = useMemo(() => {
    const stats: Record<string, { incomeCount: number; expenseCount: number; txCount: number }> = {};
    for (const m of ALL_METHODS) {
      stats[m] = {
        incomeCount:  incomes.filter(r => r.payment_method === m).length,
        expenseCount: expenses.filter(r => r.payment_method === m).length,
        txCount:      transactions.filter(r => r.payment_method === m).length,
      };
    }
    return stats;
  }, [incomes, expenses, transactions]);

  // ── Client-side filtered rows ─────────────────────────────────────────────────
  const filteredIncomes = useMemo(() =>
    activeMethodCard ? incomes.filter(r => r.payment_method === activeMethodCard) : incomes,
  [incomes, activeMethodCard]);

  const filteredExpenses = useMemo(() =>
    activeMethodCard ? expenses.filter(r => r.payment_method === activeMethodCard) : expenses,
  [expenses, activeMethodCard]);

  const filteredTransactions = useMemo(() =>
    activeMethodCard ? transactions.filter(r => r.payment_method === activeMethodCard) : transactions,
  [transactions, activeMethodCard]);

  const handleMethodClick = (method: string) => {
    setActiveMethodCard(prev => prev === method ? null : method);
  };

  const refetchAll = () => { refetchSummary(); refetchIncome(); refetchExpenses(); refetchTx(); };

  const [pdfLoading, setPdfLoading] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;
  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const params: Record<string, string> = {};
      if (driverId)  params.driver_id = driverId;
      if (dateFrom)  params.date_from = dateFrom;
      if (dateTo)    params.date_to   = dateTo;
      const response = await axios.get(`${API_URL}/pdf/driver-payroll-report`, {
        params, responseType: "blob",
        headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
      });
      const url  = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `driver-payroll-${new Date().toISOString().slice(0, 10)}.pdf`);
      document.body.appendChild(link); link.click(); link.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 10000);
    } catch { /* silent */ } finally { setPdfLoading(false); }
  };

  const handleExportToExcel = () => {
    const wb = XLSX.utils.book_new();

    const incomeData = filteredIncomes.map((r, i) => ({
      "#":             i + 1,
      "Invoice No":    r.invoice_no || "",
      "Driver":        r.driver_name || "",
      "Amount":        Number(r.total_payment || 0).toFixed(2),
      "Method":        r.payment_method || "",
      "Date":          r.trip_date ? dayjs(r.trip_date).format("DD MMM YYYY") : "",
      "Notes":         r.payment_notes || "",
    }));
    if (incomeData.length > 0) {
      const ws = XLSX.utils.json_to_sheet(incomeData);
      ws["!cols"] = Object.keys(incomeData[0]).map((k) => ({ wch: Math.max(k.length, ...incomeData.map((r: any) => String(r[k] || "").length)) + 2 }));
      XLSX.utils.book_append_sheet(wb, ws, "Income");
    }

    const expenseData = filteredExpenses.map((r, i) => ({
      "#":           i + 1,
      "Type":        r.expense_type || "",
      "Amount":      Number(r.amount || 0).toFixed(2),
      "Method":      r.payment_method || "",
      "Date":        r.expense_date ? dayjs(r.expense_date).format("DD MMM YYYY") : "",
      "Notes":       r.notes || "",
    }));
    if (expenseData.length > 0) {
      const ws2 = XLSX.utils.json_to_sheet(expenseData);
      ws2["!cols"] = Object.keys(expenseData[0]).map((k) => ({ wch: Math.max(k.length, ...expenseData.map((r: any) => String(r[k] || "").length)) + 2 }));
      XLSX.utils.book_append_sheet(wb, ws2, "Expenses");
    }

    const txData = filteredTransactions.map((r, i) => ({
      "#":           i + 1,
      "Type":        r.type || "",
      "Description": r.description || "",
      "Amount":      Number(r.amount || 0).toFixed(2),
      "Method":      r.payment_method || "",
      "Date":        r.date ? dayjs(r.date).format("DD MMM YYYY") : "",
    }));
    if (txData.length > 0) {
      const ws3 = XLSX.utils.json_to_sheet(txData);
      ws3["!cols"] = Object.keys(txData[0]).map((k) => ({ wch: Math.max(k.length, ...txData.map((r: any) => String(r[k] || "").length)) + 2 }));
      XLSX.utils.book_append_sheet(wb, ws3, "Transactions");
    }

    if (wb.SheetNames.length === 0) XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet([]), "Payroll");
    XLSX.writeFile(wb, `driver_payroll_${dayjs().format("YYYYMMDD")}.xlsx`);
  };

  const handleDeleteExpense = async (id: number) => {
    const res: any = await deleteExpense(id);
    if (res?.error || res?.data?.success === false) {
      showErrorMessage("Failed to delete expense.");
    } else {
      showMessage("Expense deleted.");
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography sx={{ fontSize: 24, fontWeight: 800, color: "#0f172a" }}>Driver Pay Roll</Typography>
        <Typography sx={{ fontSize: 13, color: "#64748b", mt: 0.5 }}>
          Income from trips · Expenses · Method breakdown · Net balance
        </Typography>
      </Box>

      {/* Filters */}
      <Card elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2.5, p: 2, mb: 3 }}>
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end", flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, color: "#64748b", mb: 0.25 }}>
            <FilterListIcon sx={{ fontSize: 16 }} />
            <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em" }}>Filters</Typography>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#64748b", mb: 0.5 }}>Driver</Typography>
            <Select size="small" value={driverId} onChange={(e) => setDriverId(e.target.value)} displayEmpty
              sx={{ minWidth: 200, borderRadius: 2, fontSize: 13, height: 36, bgcolor: "#fff" }}>
              <MenuItem value=""><em>All Drivers</em></MenuItem>
              {drivers.map((d: any) => (
                <MenuItem key={d.id} value={String(d.id)} sx={{ fontSize: 13 }}>
                  {fmtDriverName(d)}
                </MenuItem>
              ))}
            </Select>
          </Box>

          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#64748b", mb: 0.5 }}>From</Typography>
            <TextField type="date" size="small" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 148, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, height: 36 } }} />
          </Box>

          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#64748b", mb: 0.5 }}>To</Typography>
            <TextField type="date" size="small" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: dateFrom || undefined }}
              sx={{ width: 148, "& .MuiOutlinedInput-root": { borderRadius: 2, fontSize: 13, height: 36 } }} />
          </Box>

          <Box>
            <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#64748b", mb: 0.5 }}>Payment Method</Typography>
            <Select size="small" value={methodFilter} onChange={(e) => { setMethodFilter(e.target.value); setActiveMethodCard(null); }} displayEmpty
              sx={{ minWidth: 155, borderRadius: 2, fontSize: 13, height: 36, bgcolor: "#fff" }}>
              <MenuItem value=""><em>All Methods</em></MenuItem>
              {ALL_METHODS.map((m) => <MenuItem key={m} value={m} sx={{ fontSize: 13 }}>{METHOD_CFG[m].label}</MenuItem>)}
            </Select>
          </Box>

          {(driverId || dateFrom || dateTo || methodFilter || activeMethodCard) && (
            <Button size="small" onClick={() => { setDriverId(""); setDateFrom(""); setDateTo(""); setMethodFilter(""); setActiveMethodCard(null); }}
              sx={{ height: 36, borderRadius: 2, textTransform: "none", fontSize: 12, color: "#64748b", border: `1px solid ${BORDER}`, mt: 2.5 }}>
              Clear All
            </Button>
          )}
          <Button size="small" variant="outlined"
            startIcon={pdfLoading ? <CircularProgress size={13} color="inherit" /> : <PrintIcon sx={{ fontSize: 15 }} />}
            onClick={handleDownloadPdf} disabled={pdfLoading}
            sx={{ height: 36, borderRadius: 2, textTransform: "none", fontSize: 12, color: "#64748b", border: `1px solid ${BORDER}`, mt: 2.5 }}>
            {pdfLoading ? "Generating…" : "Print"}
          </Button>
          <Button size="small" variant="outlined"
            startIcon={<TableChartOutlinedIcon sx={{ fontSize: 15 }} />}
            onClick={handleExportToExcel}
            sx={{ height: 36, borderRadius: 2, textTransform: "none", fontSize: 12, color: "#16a34a", border: `1px solid ${BORDER}`, mt: 2.5 }}>
            Excel
          </Button>
        </Box>
      </Card>

      {/* Summary cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{ border: `1px solid #bbf7d0`, borderRadius: 2.5, p: 2.5, bgcolor: "#f0fdf4" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <TrendingUpIcon sx={{ fontSize: 20, color: "#16a34a" }} />
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#16a34a", textTransform: "uppercase", letterSpacing: "0.06em" }}>Total Income</Typography>
            </Box>
            <Typography sx={{ fontSize: 32, fontWeight: 900, color: "#16a34a" }}>
              ${(summary?.total_income ?? 0).toFixed(2)}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#4ade80", mt: 0.5 }}>{incomes.length} payment{incomes.length !== 1 ? "s" : ""}</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card elevation={0} sx={{ border: `1px solid #fecaca`, borderRadius: 2.5, p: 2.5, bgcolor: "#fef2f2" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <TrendingDownIcon sx={{ fontSize: 20, color: "#ef4444" }} />
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#ef4444", textTransform: "uppercase", letterSpacing: "0.06em" }}>Total Expenses</Typography>
            </Box>
            <Typography sx={{ fontSize: 32, fontWeight: 900, color: "#ef4444" }}>
              ${(summary?.total_expense ?? 0).toFixed(2)}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#f87171", mt: 0.5 }}>{expenses.length} expense{expenses.length !== 1 ? "s" : ""}</Typography>
          </Card>
        </Grid>
        <Grid item xs={12} sm={4}>
          <Card elevation={0}
            sx={{ border: `1px solid ${(summary?.net_balance ?? 0) >= 0 ? "#c7d2fe" : "#fecaca"}`,
              borderRadius: 2.5, p: 2.5,
              bgcolor: (summary?.net_balance ?? 0) >= 0 ? "#eef2ff" : "#fef2f2" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
              <AccountBalanceWalletOutlinedIcon sx={{ fontSize: 20, color: BRAND }} />
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: BRAND, textTransform: "uppercase", letterSpacing: "0.06em" }}>Net Balance</Typography>
            </Box>
            <Typography sx={{ fontSize: 32, fontWeight: 900, color: (summary?.net_balance ?? 0) >= 0 ? "#16a34a" : "#ef4444" }}>
              ${(summary?.net_balance ?? 0).toFixed(2)}
            </Typography>
            <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.5 }}>Income − Expenses</Typography>
          </Card>
        </Grid>
      </Grid>

      {/* Method breakdown — interactive cards */}
      <Card elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2.5, p: 2.5, mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>Payment Method Breakdown</Typography>
            <Typography sx={{ fontSize: 11, color: "#94a3b8", mt: 0.25 }}>Click a card to filter Income, Expenses &amp; Transactions instantly</Typography>
          </Box>
          {activeMethodCard && (
            <Button size="small" onClick={() => setActiveMethodCard(null)}
              sx={{ fontSize: 11, textTransform: "none", color: "#64748b", border: `1px solid ${BORDER}`, borderRadius: 2, height: 28, px: 1.5 }}>
              Show All
            </Button>
          )}
        </Box>
        <Grid container spacing={1.5}>
          {ALL_METHODS.map((m) => {
            const b        = summary?.breakdown?.[m] ?? { income: 0, expense: 0, balance: 0 };
            const cfg      = METHOD_CFG[m];
            const stats    = methodStats[m];
            const isActive = activeMethodCard === m;
            return (
              <Grid item xs={12} sm={6} md key={m}>
                <Tooltip title={isActive ? `Click to deselect ${cfg.label}` : `Click to filter by ${cfg.label}`} placement="top">
                  <Box
                    onClick={() => handleMethodClick(m)}
                    sx={{
                      border: isActive ? `2px solid ${cfg.activeBorder}` : `1px solid ${cfg.border}`,
                      borderRadius: 2, p: 1.5,
                      bgcolor: isActive ? cfg.bg : "#fff",
                      cursor: "pointer",
                      position: "relative",
                      boxShadow: isActive ? `0 0 0 4px ${cfg.glow}, 0 4px 16px ${cfg.glow}` : "0 1px 3px rgba(0,0,0,0.04)",
                      transform: isActive ? "translateY(-3px) scale(1.02)" : "none",
                      transition: "all 0.2s cubic-bezier(0.4,0,0.2,1)",
                      "&:hover": {
                        border: `2px solid ${cfg.activeBorder}`,
                        boxShadow: `0 0 0 3px ${cfg.glow}, 0 6px 20px ${cfg.glow}`,
                        transform: "translateY(-2px)",
                        bgcolor: cfg.bg,
                      },
                    }}
                  >
                    {/* Active indicator dot */}
                    {isActive && (
                      <Box sx={{ position: "absolute", top: -5, right: -5, width: 14, height: 14, borderRadius: "50%", bgcolor: cfg.color, border: "2px solid #fff", display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: "#fff" }} />
                      </Box>
                    )}
                    {/* Label + icon */}
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Box sx={{ fontSize: 14 }}>{cfg.icon}</Box>
                        <Typography sx={{ fontSize: 11, fontWeight: 700, color: isActive ? cfg.color : "#374151" }}>{cfg.label}</Typography>
                      </Box>
                      {/* Transaction count badge */}
                      <Box sx={{ display: "inline-flex", alignItems: "center", px: 0.75, py: 0.1, borderRadius: 1, bgcolor: isActive ? alpha(cfg.color, 0.12) : "#f1f5f9", border: `1px solid ${isActive ? cfg.border : "#e2e8f0"}` }}>
                        <Typography sx={{ fontSize: 10, fontWeight: 800, color: isActive ? cfg.color : "#64748b" }}>
                          {(stats.incomeCount + stats.expenseCount)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.4 }}>
                      <Typography sx={{ fontSize: 11, color: "#64748b" }}>Income</Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#16a34a" }}>+${b.income.toFixed(2)}</Typography>
                    </Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.75 }}>
                      <Typography sx={{ fontSize: 11, color: "#64748b" }}>Expenses</Typography>
                      <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#ef4444" }}>−${b.expense.toFixed(2)}</Typography>
                    </Box>
                    <Divider sx={{ borderColor: isActive ? cfg.border : "#f1f5f9", mb: 0.75 }} />
                    <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#374151" }}>Balance</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 900, color: b.balance >= 0 ? "#16a34a" : "#ef4444" }}>
                        ${b.balance.toFixed(2)}
                      </Typography>
                    </Box>
                  </Box>
                </Tooltip>
              </Grid>
            );
          })}
        </Grid>
      </Card>

      {/* Section tabs */}
      <Box sx={{ display: "flex", gap: 0, borderBottom: `2px solid ${BORDER}`, mb: 2 }}>
        {([
          { key: "income",       label: "Income",       icon: <TrendingUpIcon sx={{ fontSize: 15 }} />,       count: activeMethodCard ? filteredIncomes.length       : null },
          { key: "expenses",     label: "Expenses",     icon: <TrendingDownIcon sx={{ fontSize: 15 }} />,     count: activeMethodCard ? filteredExpenses.length      : null },
          { key: "transactions", label: "Transactions", icon: <PaymentsOutlinedIcon sx={{ fontSize: 15 }} />, count: activeMethodCard ? filteredTransactions.length  : null },
        ] as const).map((tab) => (
          <Button key={tab.key} size="small" onClick={() => setActiveSection(tab.key)}
            startIcon={tab.icon}
            sx={{
              textTransform: "none", fontSize: 13, fontWeight: 600, borderRadius: 0, pb: 1.25, px: 2,
              borderBottom: activeSection === tab.key ? `2px solid ${BRAND}` : "2px solid transparent",
              mb: "-2px", color: activeSection === tab.key ? BRAND : "#64748b",
            }}>
            {tab.label}
            {tab.count !== null && (
              <Box component="span" sx={{ ml: 0.75, px: 0.75, py: 0.1, borderRadius: 1, fontSize: 10, fontWeight: 800, bgcolor: BRAND, color: "#fff" }}>
                {tab.count}
              </Box>
            )}
          </Button>
        ))}
      </Box>

      {/* ── INCOME TABLE ─────────────────────────────────────────────────────── */}
      {activeSection === "income" && (
        <Card elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2.5, overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: "#f0fdf4", borderBottom: "1px solid #bbf7d0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TrendingUpIcon sx={{ fontSize: 16, color: "#16a34a" }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#16a34a" }}>
                Income — Completed Trips
                <Box component="span" sx={{ ml: 1, fontSize: 11, fontWeight: 500, color: "#4ade80" }}>
                  ({filteredIncomes.length}{incomes.length !== filteredIncomes.length ? ` of ${incomes.length}` : ""})
                </Box>
              </Typography>
            </Box>
          </Box>
          {/* Drill-down header */}
          {activeMethodCard && (
            <Fade in>
              <Box sx={{
                px: 2.5, py: 1.25,
                bgcolor: alpha(METHOD_CFG[activeMethodCard as PaymentMethod]?.color ?? BRAND, 0.06),
                borderBottom: `1px solid ${METHOD_CFG[activeMethodCard as PaymentMethod]?.border ?? BORDER}`,
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FilterListIcon sx={{ fontSize: 14, color: METHOD_CFG[activeMethodCard as PaymentMethod]?.color ?? BRAND }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: METHOD_CFG[activeMethodCard as PaymentMethod]?.color ?? BRAND }}>
                    Showing: {METHOD_CFG[activeMethodCard as PaymentMethod]?.icon} {METHOD_CFG[activeMethodCard as PaymentMethod]?.label} Transactions{" "}
                    <Box component="span" sx={{ fontWeight: 900 }}>({filteredIncomes.length} record{filteredIncomes.length !== 1 ? "s" : ""})</Box>
                  </Typography>
                </Box>
                <Button size="small" onClick={() => setActiveMethodCard(null)}
                  sx={{ fontSize: 10, textTransform: "none", color: "#64748b", height: 24, px: 1 }}>Clear</Button>
              </Box>
            </Fade>
          )}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["#", "Invoice / Trip", "Driver", "Date", "Hours", "Rate", "Base", "Tips", "Total", "Payment Method", "Status"].map((h) => (
                    <TableCell key={h} sx={thSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredIncomes.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={11} sx={{ textAlign: "center", py: 5, color: "#94a3b8", fontSize: 13 }}>
                      {activeMethodCard ? `No ${METHOD_CFG[activeMethodCard as PaymentMethod]?.label} income records found.` : "No income records found."}
                      {activeMethodCard && (
                        <Button size="small" onClick={() => setActiveMethodCard(null)} sx={{ ml: 1, fontSize: 11, textTransform: "none", color: BRAND }}>Clear filter</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : filteredIncomes.map((p, i) => (
                  <TableRow key={p.id} sx={{ "&:hover": { bgcolor: "#f8fafc" }, "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell sx={{ ...tdSx, color: "#94a3b8" }}>{i + 1}</TableCell>
                    <TableCell sx={tdSx}><Typography sx={{ fontSize: 12, fontWeight: 700, color: BRAND }}>{p.invoice_no || "—"}</Typography></TableCell>
                    <TableCell sx={tdSx}><Typography sx={{ fontSize: 12, fontWeight: 600 }}>{p.driver_name || "—"}</Typography></TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: 12 }}>{p.trip_date ? dayjs(p.trip_date).format("DD MMM YYYY") : "—"}</Typography>
                    </TableCell>
                    <TableCell sx={tdSx}><Typography sx={{ fontSize: 12, fontWeight: 600 }}>{p.hours_worked}h</Typography></TableCell>
                    <TableCell sx={tdSx}><Typography sx={{ fontSize: 12 }}>${p.hourly_rate}/h</Typography></TableCell>
                    <TableCell sx={tdSx}><Typography sx={{ fontSize: 12 }}>${p.base_salary.toFixed(2)}</Typography></TableCell>
                    <TableCell sx={tdSx}><Typography sx={{ fontSize: 12 }}>${p.tips.toFixed(2)}</Typography></TableCell>
                    <TableCell sx={tdSx}><Typography sx={{ fontSize: 14, fontWeight: 800, color: "#16a34a" }}>${p.total_payment.toFixed(2)}</Typography></TableCell>
                    <TableCell sx={tdSx}>
                      <Tooltip title="Click to set/change payment method" arrow>
                        <span><MethodChip method={p.payment_method} onClick={() => setPaymentIncome(p)} /></span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Chip label={p.status === "paid" ? "Paid" : "Pending"} size="small"
                        sx={{ height: 20, fontSize: 10, fontWeight: 700,
                          color: p.status === "paid" ? "#16a34a" : "#f59e0b",
                          bgcolor: p.status === "paid" ? "#f0fdf4" : "#fffbeb",
                          border: `1px solid ${p.status === "paid" ? "#bbf7d0" : "#fde68a"}` }} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* ── EXPENSES TABLE ───────────────────────────────────────────────────── */}
      {activeSection === "expenses" && (
        <Card elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2.5, overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: "#fef2f2", borderBottom: "1px solid #fecaca",
            display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <TrendingDownIcon sx={{ fontSize: 16, color: "#ef4444" }} />
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#ef4444" }}>
                Expenses
                <Box component="span" sx={{ ml: 1, fontSize: 11, fontWeight: 500, color: "#f87171" }}>
                  ({filteredExpenses.length}{expenses.length !== filteredExpenses.length ? ` of ${expenses.length}` : ""})
                </Box>
              </Typography>
            </Box>
            <Button size="small" variant="contained" startIcon={<AddIcon />}
              disabled={!driverId}
              onClick={() => setExpenseOpen(true)}
              sx={{ borderRadius: 2, textTransform: "none", fontSize: 12,
                bgcolor: "#ef4444", "&:hover": { bgcolor: "#dc2626" },
                "&:disabled": { bgcolor: "#fca5a5", color: "#fff" } }}>
              + Add Expense
            </Button>
          </Box>
          {!driverId && (
            <Box sx={{ px: 2.5, py: 1.25, bgcolor: "#fffbeb", borderBottom: "1px solid #fde68a" }}>
              <Typography sx={{ fontSize: 12, color: "#92400e" }}>Select a driver above to add expenses.</Typography>
            </Box>
          )}
          {/* Drill-down header */}
          {activeMethodCard && (
            <Fade in>
              <Box sx={{
                px: 2.5, py: 1.25,
                bgcolor: alpha(METHOD_CFG[activeMethodCard as PaymentMethod]?.color ?? BRAND, 0.06),
                borderBottom: `1px solid ${METHOD_CFG[activeMethodCard as PaymentMethod]?.border ?? BORDER}`,
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FilterListIcon sx={{ fontSize: 14, color: METHOD_CFG[activeMethodCard as PaymentMethod]?.color ?? BRAND }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: METHOD_CFG[activeMethodCard as PaymentMethod]?.color ?? BRAND }}>
                    Showing: {METHOD_CFG[activeMethodCard as PaymentMethod]?.icon} {METHOD_CFG[activeMethodCard as PaymentMethod]?.label} Expenses{" "}
                    <Box component="span" sx={{ fontWeight: 900 }}>({filteredExpenses.length} record{filteredExpenses.length !== 1 ? "s" : ""})</Box>
                  </Typography>
                </Box>
                <Button size="small" onClick={() => setActiveMethodCard(null)}
                  sx={{ fontSize: 10, textTransform: "none", color: "#64748b", height: 24, px: 1 }}>Clear</Button>
              </Box>
            </Fade>
          )}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["#", "Type", "Amount", "Payment Method", "Date", "Notes", "Action"].map((h) => (
                    <TableCell key={h} sx={thSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredExpenses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: "center", py: 5, color: "#94a3b8", fontSize: 13 }}>
                      {activeMethodCard ? `No ${METHOD_CFG[activeMethodCard as PaymentMethod]?.label} expenses found.` : "No expenses recorded."}
                      {activeMethodCard && (
                        <Button size="small" onClick={() => setActiveMethodCard(null)} sx={{ ml: 1, fontSize: 11, textTransform: "none", color: BRAND }}>Clear filter</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : filteredExpenses.map((e, i) => (
                  <TableRow key={e.id} sx={{ "&:hover": { bgcolor: "#fef2f2" }, "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell sx={{ ...tdSx, color: "#94a3b8" }}>{i + 1}</TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#0f172a", textTransform: "capitalize" }}>
                        {e.expense_type}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: 14, fontWeight: 800, color: "#ef4444" }}>−${e.amount.toFixed(2)}</Typography>
                    </TableCell>
                    <TableCell sx={tdSx}><MethodChip method={e.payment_method} /></TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: 12 }}>{dayjs(e.expense_date).format("DD MMM YYYY")}</Typography>
                    </TableCell>
                    <TableCell sx={{ ...tdSx, maxWidth: 200 }}>
                      <Typography sx={{ fontSize: 12, color: "#64748b" }}>{e.notes || "—"}</Typography>
                    </TableCell>
                    <TableCell sx={{ ...tdSx, textAlign: "right" }}>
                      <Tooltip title="Delete expense" arrow>
                        <IconButton size="small" onClick={() => handleDeleteExpense(e.id)}
                          sx={{ width: 26, height: 26, borderRadius: 1.5, color: "#ef4444", "&:hover": { bgcolor: "#fef2f2" } }}>
                          <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* ── TRANSACTIONS TABLE ───────────────────────────────────────────────── */}
      {activeSection === "transactions" && (
        <Card elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2.5, overflow: "hidden" }}>
          <Box sx={{ px: 2, py: 1.5, bgcolor: "#f8fafc", borderBottom: `1px solid ${BORDER}`, display: "flex", alignItems: "center", gap: 1 }}>
            <PaymentsOutlinedIcon sx={{ fontSize: 16, color: BRAND }} />
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>
              All Transactions — Running Balance
              <Box component="span" sx={{ ml: 1, fontSize: 11, fontWeight: 500, color: "#94a3b8" }}>
                ({filteredTransactions.length}{transactions.length !== filteredTransactions.length ? ` of ${transactions.length}` : ""})
              </Box>
            </Typography>
          </Box>
          {/* Drill-down header */}
          {activeMethodCard && (
            <Fade in>
              <Box sx={{
                px: 2.5, py: 1.25,
                bgcolor: alpha(METHOD_CFG[activeMethodCard as PaymentMethod]?.color ?? BRAND, 0.06),
                borderBottom: `1px solid ${METHOD_CFG[activeMethodCard as PaymentMethod]?.border ?? BORDER}`,
                display: "flex", alignItems: "center", justifyContent: "space-between",
              }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <FilterListIcon sx={{ fontSize: 14, color: METHOD_CFG[activeMethodCard as PaymentMethod]?.color ?? BRAND }} />
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: METHOD_CFG[activeMethodCard as PaymentMethod]?.color ?? BRAND }}>
                    Showing: {METHOD_CFG[activeMethodCard as PaymentMethod]?.icon} {METHOD_CFG[activeMethodCard as PaymentMethod]?.label} Transactions{" "}
                    <Box component="span" sx={{ fontWeight: 900 }}>({filteredTransactions.length} record{filteredTransactions.length !== 1 ? "s" : ""})</Box>
                  </Typography>
                </Box>
                <Button size="small" onClick={() => setActiveMethodCard(null)}
                  sx={{ fontSize: 10, textTransform: "none", color: "#64748b", height: 24, px: 1 }}>Clear</Button>
              </Box>
            </Fade>
          )}
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  {["Date", "Type", "Description", "Method", "Amount", "Balance After"].map((h) => (
                    <TableCell key={h} sx={thSx}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTransactions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} sx={{ textAlign: "center", py: 5, color: "#94a3b8", fontSize: 13 }}>
                      {activeMethodCard ? `No ${METHOD_CFG[activeMethodCard as PaymentMethod]?.label} transactions yet.` : "No transactions yet."}
                      {activeMethodCard && (
                        <Button size="small" onClick={() => setActiveMethodCard(null)} sx={{ ml: 1, fontSize: 11, textTransform: "none", color: BRAND }}>Clear filter</Button>
                      )}
                    </TableCell>
                  </TableRow>
                ) : filteredTransactions.map((tx) => (
                  <TableRow key={tx.id} sx={{ "&:hover": { bgcolor: "#f8fafc" }, "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: 12 }}>{tx.date ? dayjs(tx.date).format("DD MMM YYYY") : "—"}</Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Box component="span"
                        sx={{ display: "inline-flex", px: 1.25, py: 0.3, borderRadius: 1.5, fontSize: 11, fontWeight: 700,
                          color: tx.type === "income" ? "#16a34a" : "#ef4444",
                          bgcolor: tx.type === "income" ? "#f0fdf4" : "#fef2f2",
                          border: `1px solid ${tx.type === "income" ? "#bbf7d0" : "#fecaca"}` }}>
                        {tx.type === "income" ? "↑ Income" : "↓ Expense"}
                      </Box>
                    </TableCell>
                    <TableCell sx={{ ...tdSx, maxWidth: 220 }}>
                      <Typography sx={{ fontSize: 12, color: "#374151" }}>{tx.description}</Typography>
                    </TableCell>
                    <TableCell sx={tdSx}><MethodChip method={tx.payment_method} /></TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: 13, fontWeight: 800, color: tx.type === "income" ? "#16a34a" : "#ef4444" }}>
                        {tx.type === "income" ? "+" : "−"}${tx.amount.toFixed(2)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={tdSx}>
                      <Typography sx={{ fontSize: 14, fontWeight: 900, color: tx.balance_after >= 0 ? "#16a34a" : "#ef4444" }}>
                        ${tx.balance_after.toFixed(2)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      {/* Modals */}
      <ProcessPaymentModal income={paymentIncome} onClose={() => setPaymentIncome(null)} onSaved={refetchAll} />
      <AddExpenseModal open={expenseOpen} driverId={driverId} onClose={() => setExpenseOpen(false)} onSaved={refetchAll} />
    </Box>
  );
};

export default DriverPayrollPage;
